import {
  Alert02Icon,
  CheckmarkCircle02Icon,
  GitBranchIcon,
  GitMergeIcon,
  Rocket01Icon,
  Task01Icon,
  Tick02Icon,
  UserCheck01Icon,
} from '@hugeicons/core-free-icons'
import { HugeiconsIcon } from '@hugeicons/react'

import { cn } from '@/lib/utils'

const CAMINHO = 'M70 80 L330 80 C395 80 395 210 330 210 L120 210'

type Etapa = {
  id: string
  nome: string
  icone: Parameters<typeof HugeiconsIcon>[0]['icon']
  x: number
  y: number
}

const etapas: Etapa[] = [
  { id: 'issue', nome: 'Issue', icone: Task01Icon, x: 70, y: 80 },
  { id: 'branch', nome: 'Branch', icone: GitBranchIcon, x: 200, y: 80 },
  { id: 'mr', nome: 'MR', icone: GitMergeIcon, x: 330, y: 80 },
  { id: 'revisao', nome: 'Revisão', icone: UserCheck01Icon, x: 200, y: 210 },
  { id: 'deploy', nome: 'Deploy', icone: Rocket01Icon, x: 70, y: 210 },
]

const pos = (x: number, y: number) => ({ left: `${(x / 400) * 100}%`, top: `${(y / 290) * 100}%` })

/**
 * Ilustração do login: o trabalho passa pelas etapas do GitLab enquanto o
 * Heimdall acompanha. Na MR ele encontra um desvio, orienta, e a etapa segue.
 */
export function FluxoVigiado({ className }: { className?: string }) {
  return (
    <div
      role="img"
      aria-label="Uma tarefa passa por issue, branch, MR, revisão e deploy, e cada etapa é conferida. Na MR o Heimdall aponta que falta evidência de teste; o fluxo espera a correção e depois segue até o deploy."
      className={cn('relative aspect-[400/290] w-full select-none', className)}
    >
      <svg
        viewBox="0 0 400 290"
        className="absolute inset-0 size-full overflow-visible"
        aria-hidden
      >
        <path
          d={CAMINHO}
          fill="none"
          stroke="currentColor"
          strokeOpacity={0.22}
          strokeWidth={2}
          strokeDasharray="2 7"
          strokeLinecap="round"
        />
        <path
          d={CAMINHO}
          fill="none"
          stroke="currentColor"
          strokeOpacity={0.75}
          strokeWidth={2}
          strokeLinecap="round"
          className="fluxo-rastro"
          pathLength={100}
        />
        <g className="fluxo-pulso">
          <circle r={14} fill="currentColor" fillOpacity={0.14} />
          <circle r={7} fill="currentColor" fillOpacity={0.3} />
          <circle r={3.5} fill="currentColor" />
        </g>
      </svg>

      {etapas.map((etapa) => (
        <div
          key={etapa.nome}
          className={cn(
            'absolute flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-full bg-card p-1.5 text-card-foreground shadow-(--heimdall-sombra) sm:pr-3.5',
            etapa.nome === 'MR' && 'fluxo-no-mr'
          )}
          style={pos(etapa.x, etapa.y)}
        >
          <span className="flex size-7 items-center justify-center rounded-full bg-primary/10 text-primary">
            <HugeiconsIcon icon={etapa.icone} strokeWidth={2} className="size-4" />
          </span>
          <span className="sr-only text-sm font-medium sm:not-sr-only">{etapa.nome}</span>
          <span
            aria-hidden
            className={`fluxo-check fluxo-check-${etapa.id} absolute -top-1.5 -right-1.5 flex size-5 items-center justify-center rounded-full bg-chart-sem-success text-chart-cinza-escuro ring-2 ring-primary`}
          >
            <HugeiconsIcon icon={Tick02Icon} strokeWidth={3} className="size-3" />
          </span>
        </div>
      ))}

      <div className="absolute -translate-x-1/2" style={{ ...pos(330, 80), marginTop: '1.75rem' }}>
        <div className="relative">
          <span className="fluxo-alerta absolute top-0 left-1/2 flex -translate-x-3/4 items-center gap-1.5 rounded-full bg-chart-sem-warning px-2.5 py-1 text-[0.7rem] font-medium whitespace-nowrap text-chart-cinza-escuro sm:text-xs">
            <HugeiconsIcon icon={Alert02Icon} strokeWidth={2} className="size-3.5" />
            Falta evidência de teste
          </span>
          <span className="fluxo-ok absolute top-0 left-1/2 flex -translate-x-3/4 items-center gap-1.5 rounded-full bg-chart-sem-success px-2.5 py-1 text-[0.7rem] font-medium whitespace-nowrap text-chart-cinza-escuro sm:text-xs">
            <HugeiconsIcon icon={CheckmarkCircle02Icon} strokeWidth={2} className="size-3.5" />
            Corrigido
          </span>
        </div>
      </div>
    </div>
  )
}
